import { createScopedMemory } from '~/utils/scopedMemory'

/**
 * Unified, hierarchical view memory for the followups page. One localStorage
 * key holds every level; each recall/remember is keyed by its resolved parents,
 * so each context remembers its own last selection:
 *
 *   company      (global)                      → last company
 *   leadAgency   (companyId)                   → last LA in that company
 *   planningDay  (companyId|leadAgencyId)      → last day view in that (C, LA)
 *   filters      (companyId|leadAgencyId|day)  → last filters in that day view
 *   case         (companyId|leadAgencyId|day)  → last selected case in that day
 *
 * Module-level singleton: the nav composable, the store and the page all share
 * one source of truth. Backed by `createScopedMemory` (localStorage today;
 * swap for a server store later without touching call sites).
 */
const memory = createScopedMemory('followupsView.v1')

const LEVEL = {
  company: 'company',
  leadAgency: 'leadAgency',
  planningDay: 'planningDay',
  filters: 'filters',
  case: 'case',
}

const GLOBAL = 'global'
const laScope = c => String(c ?? '')
const dayScope = (c, la) => `${c ?? ''}|${la ?? ''}`

// Case + filters live one level deeper, per (company, LA, planning-day view).
const leafScope = (c, la, dayKey) => `${c ?? ''}|${la ?? ''}|${dayKey ?? ''}`

/**
 * Serialises a `planFilter` ({ kind, iso }) into a stable scope segment so the
 * filters + case memory is keyed by the exact day view.
 */
export function planFilterKey(planFilter) {
  if (!planFilter || typeof planFilter !== 'object') return 'all:'

  return `${planFilter.kind ?? 'all'}:${planFilter.iso ?? ''}`
}

export function useFollowupsViewMemory() {
  return {
    // Level 0 — last company (global).
    recallCompany: () => memory.recall(LEVEL.company, GLOBAL),
    rememberCompany(id) {
      if (id != null) memory.remember(LEVEL.company, GLOBAL, String(id))
    },

    // Level 1 — last lead agency per company.
    recallLeadAgency: c => memory.recall(LEVEL.leadAgency, laScope(c)),
    rememberLeadAgency(c, la) {
      if (c != null && la != null) {
        memory.remember(LEVEL.leadAgency, laScope(c), String(la))
      }
    },

    // Level 2 — last planning-day view per (company, LA). Stores the planFilter.
    recallPlanningDay: (c, la) => memory.recall(LEVEL.planningDay, dayScope(c, la)),
    rememberPlanningDay(c, la, planFilter) {
      if (c != null) memory.remember(LEVEL.planningDay, dayScope(c, la), planFilter)
    },

    // Level 3 — last filters per (company, LA, planning-day view).
    recallFilters: (c, la, planFilter) =>
      memory.recall(LEVEL.filters, leafScope(c, la, planFilterKey(planFilter))),
    rememberFilters(c, la, planFilter, filters) {
      if (c != null) {
        memory.remember(
          LEVEL.filters,
          leafScope(c, la, planFilterKey(planFilter)),
          filters,
        )
      }
    },

    // Selected case per (company, LA, planning-day view).
    recallCase: (c, la, planFilter) =>
      memory.recall(LEVEL.case, leafScope(c, la, planFilterKey(planFilter))),
    rememberCase(c, la, planFilter, caseId) {
      if (c != null && caseId != null) {
        memory.remember(
          LEVEL.case,
          leafScope(c, la, planFilterKey(planFilter)),
          String(caseId),
        )
      }
    },

    clear: () => memory.clear(),
  }
}
